// src/pages/admin/smtpSettings.tsx - Shopify-style design with support email and contact number
import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  Chip,
  Stack,
  InputAdornment,
  IconButton as MuiIconButton,
} from '@mui/material';
import { Edit, ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useGetSmtpSettingsQuery,
  useUpdateSmtpSettingsMutation,
  SmtpSettings,
  UpdateSmtpSettingsRequest,
} from '../../features/admin/smtpSettingsApi.ts';

interface SmtpFormData {
  host: string;
  port: number;
  user: string;
  pass: string;
  from_name: string;
  from_email: string;
  support_email: string;
  contact: string;
  secure: string;
}

interface SmtpFormErrors {
  [key: string]: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const SmtpSettingsManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ 
    open: false, 
    message: '', 
    severity: 'success'
  });
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [smtpId, setSmtpId] = useState<number>(1);
  const [createdAt, setCreatedAt] = useState<string>('');
  const [formData, setFormData] = useState<SmtpFormData>({
    host: '',
    port: 465,
    user: '', 
    pass: '',
    from_name: '',
    from_email: '',
    support_email: '',
    contact: '',
    secure: 'yes',
  });
  const [formErrors, setFormErrors] = useState<SmtpFormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const { 
    data: smtpResponse, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useGetSmtpSettingsQuery();

  const [updateSmtpSettings] = useUpdateSmtpSettingsMutation();

  const smtpSettings = smtpResponse?.data;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const resetForm = () => {
    setFormData({
      host: '',
      port: 465,
      user: '',
      pass: '',
      from_name: '',
      from_email: '',
      support_email: '',
      contact: '',
      secure: 'yes',
    });
    setFormErrors({});
    setShowPassword(false);
  };

  const handleEditSmtpSettings = (settings: SmtpSettings) => {
    setFormData({
      host: settings.host || '',
      port: settings.port || 465,
      user: settings.user || '',
      pass: settings.pass || '',
      from_name: settings.from_name || '',
      from_email: settings.from_email || '',
      support_email: settings.support_email || '',
      contact: settings.contact || '',
      secure: settings.secure || 'yes',
    });
    setSmtpId(settings.id);
    setCreatedAt(settings.created_at);
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'port') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSecureChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, secure: e.target.value }));
  };

  const validateForm = (): boolean => {
    const newErrors: SmtpFormErrors = {};
    
    if (!formData.host.trim()) {
      newErrors.host = 'SMTP host is required';
    }
    
    if (!formData.port || formData.port <= 0) {
      newErrors.port = 'Valid port number is required';
    }
    
    if (!formData.user.trim()) {
      newErrors.user = 'SMTP user/email is required';
    }
    
    if (!formData.pass.trim()) {
      newErrors.pass = 'SMTP password is required';
    }
    
    if (!formData.from_name.trim()) {
      newErrors.from_name = 'From name is required';
    }
    
    if (!formData.from_email.trim()) {
      newErrors.from_email = 'From email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.from_email)) {
      newErrors.from_email = 'Invalid email format';
    }
    
    if (!formData.support_email.trim()) {
      newErrors.support_email = 'Support email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.support_email)) {
      newErrors.support_email = 'Invalid email format';
    }
    
    if (!formData.contact.trim()) {
      newErrors.contact = 'Contact number is required';
    } else if (!/^\+?[\d\s\-()]+$/.test(formData.contact)) {
      newErrors.contact = 'Invalid phone number format';
    }
    
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsFormSubmitting(true);

    try {
      const requestData: UpdateSmtpSettingsRequest = {
        id: smtpId,
        host: formData.host,
        port: formData.port,
        user: formData.user,
        pass: formData.pass,
        from_name: formData.from_name,
        from_email: formData.from_email,
        support_email: formData.support_email,
        contact: formData.contact,
        secure: formData.secure,
        created_at: createdAt,
      };

      const result = await updateSmtpSettings(requestData).unwrap();
      
      setSnackbar({
        open: true,
        message: result.message || 'SMTP updated successfully!',
        severity: 'success'
      });
      
      // Wait a moment before refetching to ensure backend has updated
      setTimeout(() => {
        refetch();
      }, 500);
      
      handleBackToList();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.data?.message || error?.message || 'Error updating SMTP settings.',
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      // Handle both ISO format and MySQL datetime format
      const date = new Date(dateString.replace(' ', 'T'));
      
      // Format: 10 Dec 2025, 11:16 am
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit',
        month: 'short', 
        year: 'numeric',
      }) + ', ' + date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Date parsing error:', error);
      return dateString;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    SMTP Settings
                  </Typography>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load SMTP settings. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && !smtpSettings && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No SMTP settings found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Configure your SMTP settings to send emails
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && smtpSettings && (
                    <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '60px 160px 80px 200px 200px 140px 100px 160px 80px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                        minWidth: '1200px'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>ID</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Host</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Port</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>From Email</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Support Email</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Contact Number</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Secure</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Last Updated</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Actions</Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '60px 160px 80px 200px 200px 140px 100px 160px 80px',
                          gap: 2,
                          px: 2,
                          py: 2,
                          alignItems: 'center',
                          '&:hover': { bgcolor: '#f9fafb' },
                          transition: 'background-color 0.15s ease',
                          minWidth: '1200px'
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#6d7175', fontWeight: 500 }}>
                          {smtpSettings.id}
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                          {smtpSettings.host || 'N/A'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          {smtpSettings.port || 'N/A'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {smtpSettings.from_email || 'N/A'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {smtpSettings.support_email || 'N/A'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {smtpSettings.contact || 'N/A'}
                        </Typography>

                        <Box>
                          {smtpSettings.secure === 'yes' ? (
                            <Chip label="Secure" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '12px' }} />
                          ) : (
                            <Chip label="Not Secure" size="small" sx={{ bgcolor: '#fff4e6', color: '#b95000', fontWeight: 600, fontSize: '12px' }} />
                          )}
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(smtpSettings.updated_at || smtpSettings.created_at)}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditSmtpSettings(smtpSettings)}
                            sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Card>
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    SMTP Settings
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Edit SMTP settings
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                          SMTP Server Configuration
                        </Typography>

                        <Stack spacing={2}>
                          <TextField
                            name="host"
                            label="SMTP Host"
                            value={formData.host}
                            onChange={handleInputChange}
                            error={!!formErrors.host}
                            helperText={formErrors.host || 'e.g., smtp.gmail.com'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                          />

                          <TextField
                            name="port"
                            label="SMTP Port"
                            type="number"
                            value={formData.port}
                            onChange={handleInputChange}
                            error={!!formErrors.port}
                            helperText={formErrors.port || 'Common ports: 465 (SSL), 587 (TLS), 25'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                          />

                          <FormControl fullWidth disabled={isFormSubmitting}>
                            <InputLabel>Security</InputLabel>
                            <Select
                              value={formData.secure}
                              onChange={handleSecureChange}
                              label="Security"
                            >
                              <MenuItem value="yes">Yes (SSL/TLS)</MenuItem>
                              <MenuItem value="no">No</MenuItem>
                            </Select>
                          </FormControl>
                        </Stack>
                      </Box>
                    </Card>

                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                          Authentication
                        </Typography>

                        <Stack spacing={2}>
                          <TextField
                            name="user"
                            label="SMTP Username/Email"
                            value={formData.user}
                            onChange={handleInputChange}
                            error={!!formErrors.user}
                            helperText={formErrors.user || 'Usually your email address'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                          />

                          <TextField
                            name="pass"
                            label="SMTP Password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.pass}
                            onChange={handleInputChange}
                            error={!!formErrors.pass}
                            helperText={formErrors.pass || 'App password or account password'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <MuiIconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </MuiIconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Stack>
                      </Box>
                    </Card>

                    <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                      <Box sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                          Contact Information
                        </Typography>

                        <Stack spacing={2}>
                          <TextField
                            name="from_name"
                            label="From Name"
                            value={formData.from_name}
                            onChange={handleInputChange}
                            error={!!formErrors.from_name}
                            helperText={formErrors.from_name || 'Name that appears in sent emails'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                          />

                          <TextField
                            name="from_email"
                            label="From Email"
                            type="email"
                            value={formData.from_email}
                            onChange={handleInputChange}
                            error={!!formErrors.from_email}
                            helperText={formErrors.from_email || 'Email address that appears as sender'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                          />

                          <TextField
                            name="support_email"
                            label="Support Email"
                            type="email"
                            value={formData.support_email}
                            onChange={handleInputChange}
                            error={!!formErrors.support_email}
                            helperText={formErrors.support_email || 'Email address for customer support inquiries'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                          />

                          <TextField
                            name="contact"
                            label="Contact Number"
                            value={formData.contact}
                            onChange={handleInputChange}
                            error={!!formErrors.contact}
                            helperText={formErrors.contact || 'Phone number for customer contact (e.g., +91 9876543210)'}
                            fullWidth
                            required
                            disabled={isFormSubmitting}
                            placeholder="+91 9876543210"
                          />
                        </Stack>
                      </Box>
                    </Card>
                  </Stack>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#202223',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          Saving...
                        </Box>
                      ) : (
                        'Save settings'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SmtpSettingsManagement;